import { DeleteOutlineRounded } from "@mui/icons-material";
import { Button, IconButton, Stack, SxProps, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import _ from "lodash";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { BorderlessTextField, Table } from "src/components";
import {
  ICreateEditMetricFormState,
  ICreateEditMetricFormStateData,
} from "src/components/metrics/CreateEditMetricForm";
import { useDestructiveConfirm } from "src/utilities";
import { v4 as newGuid } from "uuid";

interface MetricTableRow {
  rowGuid: string;
  date: DateTime;
  values: (number | null)[];
  annotation: string | null;
}

const NUMBER_NON_DATASET_COLUMNS = 3;

const columnHelper = createColumnHelper<MetricTableRow>();

// If you add a new column, make sure you update the NUMBER_NON_DATASET_COLUMNS constant
const columnsConfig = (
  form: UseFormReturn<ICreateEditMetricFormState>,
  enableActions: boolean,
  handleDateChanged: (rowGuid: string, value: DateTime) => void,
  handleDataChanged: (rowGuid: string, date: DateTime, datasetIndex: number, value: number | null) => void,
  handleAnnotationChanged: (rowGuid: string, annotation: string) => void,
  handleDeleteClicked: (row: MetricTableRow) => void
) => {
  return [
    columnHelper.accessor("date", {
      id: "Date",
      meta: {
        hideColumnMenu: true,
      },
      header: () => <Typography variant="caption">DATE</Typography>,
      size: 185,
      enableResizing: false,
      sortingFn: (a, b) => {
        return a.original.date.toMillis() - b.original.date.toMillis();
      },
      cell: (props) => {
        if (!enableActions) {
          return <Typography sx={{ pl: 0.25 }}>{props.getValue().toLocaleString(DateTime.DATE_MED)}</Typography>;
        }

        return (
          <Stack direction="row" sx={{ justifyContent: "space-between" }}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                value={props.row.original.date}
                views={["year", "month", "day"]}
                onChange={(value) => {
                  if (!_.isEmpty(value)) {
                    handleDateChanged(props.row.original.rowGuid, value);
                  }
                }}
                format="MMM d, yyyy"
                slotProps={{
                  openPickerButton: {
                    size: "small",
                  },
                }}
                sx={{
                  ".MuiPickersOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  ".Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>
        );
      },
    }),
    ...form
      .getValues("datasets")
      .filter((dataset) => dataset.guid !== undefined)
      .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
      .map((dataset, datasetIndex) => {
        return columnHelper.display({
          id: dataset.title === undefined || _.isEmpty(dataset.title) ? newGuid() : dataset.title,
          enableSorting: false,
          meta: {
            hideColumnMenu: true,
          },
          header: () => <Typography variant="caption">{dataset.title.toUpperCase()}</Typography>,
          cell: (props) => {
            if (!enableActions) {
              return <Typography>{props.row.original.values[datasetIndex]?.toLocaleString()}</Typography>;
            }

            return (
              <BorderlessTextField
                value={props.row.original.values[datasetIndex]}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => {
                  handleDataChanged(
                    props.row.original.rowGuid,
                    props.row.original.date,
                    datasetIndex,
                    _.isEmpty(e.target.value) ? null : parseFloat(e.target.value.replace(/,/g, ""))
                  );
                }}
                isEditable
                maskOptions={{
                  accept: /[\d.-]+/g,
                  format: (str) => {
                    const parsedString = (str.match(/[\d.-]+/g) || []).join("");
                    const hasTrailingDecimal = parsedString.endsWith(".");

                    if (_.isEmpty(parsedString)) {
                      return "";
                    }

                    if (parsedString === "-") {
                      return "-";
                    }

                    const number = Number(parsedString);

                    if (Number.isNaN(number)) {
                      return "";
                    }

                    const formattedNumber = number.toLocaleString();

                    return hasTrailingDecimal ? `${formattedNumber}.` : formattedNumber;
                  },
                }}
              />
            );
          },
        });
      }),
    columnHelper.display({
      id: "Notes",
      size: 185,
      enableSorting: false,
      enableResizing: false,
      enableHiding: false,
      meta: {
        hideColumnMenu: true,
      },
      header: () => <Typography variant="caption">NOTES</Typography>,
      cell: (props) => {
        if (!enableActions) {
          return <Typography>{props.row.original.annotation ?? ""}</Typography>;
        }

        return (
          <BorderlessTextField
            value={props.row.original.annotation ?? ""}
            onFocus={(e) => e.target.select()}
            onBlur={(e) => {
              handleAnnotationChanged(props.row.original.rowGuid, e.target.value);
            }}
            maxLength={256}
            isEditable
          />
        );
      },
    }),
    columnHelper.display({
      id: "Actions",
      size: 50,
      minSize: 50,
      enableSorting: false,
      enableResizing: false,
      enableHiding: false,
      meta: {
        hideColumnMenu: true,
        hideResizer: true,
      },
      cell: (props) => {
        return (
          <IconButton size="small" onClick={() => handleDeleteClicked(props.row.original)}>
            <DeleteOutlineRounded color="error" />
          </IconButton>
        );
      },
    }),
  ];
};

interface Props {
  form: UseFormReturn<ICreateEditMetricFormState>;
  enableActions: boolean;
  sx?: SxProps;
}

const MetricTable = (props: Props) => {
  const [columns, setColumns] = useState<ColumnDef<MetricTableRow, any>[]>([]);
  const [rows, setRows] = useState<MetricTableRow[]>([]);
  const confirm = useDestructiveConfirm();

  function handleAddDataClicked() {
    const rowGuid = newGuid();
    const date = DateTime.utc();

    // Update table state
    setRows((oldRows) => [
      {
        rowGuid,
        date,
        values: [],
        annotation: "",
      },
      ...oldRows,
    ]);

    // Update form state
    props.form.setValue(
      "datasets",
      props.form.getValues("datasets").map((dataset) => {
        return {
          ...dataset,
          data: [
            ...dataset.data,
            {
              rowGuid,
              date,
              value: null,
            },
          ],
        };
      })
    );

    props.form.setValue("annotations", [
      ...props.form.getValues("annotations"),
      {
        rowGuid,
        text: "",
      },
    ]);
  }

  const handleDateChanged = useCallback(
    (rowGuid: string, value: DateTime) => {
      // Update table state
      setRows((oldRows) =>
        oldRows.map((row) => {
          if (row.rowGuid === rowGuid) {
            return {
              ...row,
              date: value,
            };
          }

          return row;
        })
      );

      // Update form state
      const updatedDatasets = props.form.getValues("datasets").map((dataset) => {
        const dataIndex = dataset.data.findIndex((data) => data.rowGuid === rowGuid);

        if (dataIndex > -1) {
          return {
            ...dataset,
            data: dataset.data.map((data, index) => {
              if (index === dataIndex) {
                return {
                  ...data,
                  date: value,
                };
              }

              return data;
            }),
          };
        } else {
          return dataset;
        }
      });

      props.form.setValue("datasets", updatedDatasets);
    },
    [props.form]
  );

  const handleDataChanged = useCallback(
    (rowGuid: string, date: DateTime, datasetIndex: number, value: number | null) => {
      // Update table state
      setRows((oldRows) => {
        const newRows = oldRows.map((row) => {
          if (row.rowGuid === rowGuid) {
            const newValues = [...row.values];
            newValues[datasetIndex] = value;

            return {
              ...row,
              values: newValues,
            };
          }

          return row;
        });

        return newRows;
      });

      // Update form state
      const updatedDatasets = props.form.getValues("datasets").map((dataset, i) => {
        if (i === datasetIndex) {
          const dataIndex = dataset.data.findIndex((data) => data.rowGuid === rowGuid);

          if (dataIndex > -1) {
            return {
              ...dataset,
              data: dataset.data.map((data, j) => {
                if (j === dataIndex) {
                  return {
                    ...data,
                    value,
                  };
                }

                return data;
              }),
            };
          } else {
            const newData: ICreateEditMetricFormStateData = {
              rowGuid,
              date,
              value,
            };

            return {
              ...dataset,
              data: [...dataset.data, newData],
            };
          }
        } else {
          return dataset;
        }
      });

      props.form.setValue("datasets", updatedDatasets);
    },
    [props.form]
  );

  const handleAnnotationsChanged = useCallback(
    (rowGuid: string, annotation: string) => {
      // Update table state
      setRows((oldRows) =>
        oldRows.map((row) => {
          if (row.rowGuid === rowGuid) {
            return {
              ...row,
              annotation: annotation,
            };
          }

          return row;
        })
      );

      // Update form state
      const updatedAnnotations = props.form.getValues("annotations");
      const updatedIndex = updatedAnnotations.findIndex((a) => a.rowGuid === rowGuid);

      if (updatedIndex === -1) {
        updatedAnnotations.push({
          rowGuid: rowGuid,
          text: annotation,
        });
      } else {
        updatedAnnotations[updatedIndex].text = annotation;
      }

      props.form.setValue("annotations", updatedAnnotations);
    },
    [props.form]
  );

  const handleDeleteClicked = useCallback(
    async (row: MetricTableRow) => {
      const { confirmed } = await confirm({
        title: "Delete Row",
        content: "Are you sure you want to delete this row?",
        confirmationText: "Delete",
        cancellationText: "Cancel",
      });

      if (!confirmed) {
        return;
      }

      // Update table state
      setRows((oldRows) => oldRows.filter((x) => x.rowGuid !== row.rowGuid));

      // Update form state
      props.form.setValue(
        "datasets",
        props.form.getValues("datasets").map((dataset) => {
          return {
            ...dataset,
            data: dataset.data.filter((x) => x.rowGuid !== row.rowGuid),
          };
        })
      );
    },
    [confirm, props.form]
  );

  const loadColumns = useCallback(() => {
    const tempColumns = columnsConfig(
      props.form,
      props.enableActions,
      handleDateChanged,
      handleDataChanged,
      handleAnnotationsChanged,
      handleDeleteClicked
    );

    setColumns(tempColumns);
  }, [
    props.form,
    props.enableActions,
    handleDateChanged,
    handleDataChanged,
    handleAnnotationsChanged,
    handleDeleteClicked,
  ]);

  const loadRows = useCallback(() => {
    const tempRows: MetricTableRow[] = [];
    const annotations = props.form.getValues("annotations");

    props.form
      .getValues("datasets")
      .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
      .forEach((dataset, datasetIndex) => {
        dataset.data?.forEach((data) => {
          if (data.date) {
            const rowIndex = tempRows.findIndex((x) => x.rowGuid === data.rowGuid);

            if (rowIndex > -1) {
              tempRows[rowIndex].values[datasetIndex] = data.value;
            } else {
              let values: (number | null)[] = [];
              values[datasetIndex] = data.value;

              tempRows.push({
                rowGuid: data.rowGuid,
                date: data.date,
                values,
                annotation: annotations.find((a) => a.rowGuid === data.rowGuid)?.text || null,
              });
            }
          }
        });
      });

    setRows(tempRows);
  }, [props.form]);

  // Inital column and row setup.
  useEffect(() => {
    loadColumns();
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const subscription = props.form.watch((formState, info) => {
      if (info.name && /datasets.\d*.title/g.test(info.name)) {
        // Dataset title changed, update the columns
        loadColumns();
      } else if (info.name && /datasets.\d*.sortOrder/g.test(info.name)) {
        // Dataset sortOrder changed, update the columns and rows
        loadColumns();
        loadRows();
      } else if (formState.datasets?.length !== columns.length - NUMBER_NON_DATASET_COLUMNS) {
        // Dataset was added or removed, update the columns
        // We need to subtract the NUMBER_NON_DATASET_COLUMNS from total columns to calculate how many dataset columns there are
        loadColumns();
        loadRows();
      }
    });

    return () => subscription.unsubscribe();
  }, [columns.length, loadColumns, loadRows, props.form]);

  return (
    <>
      <Button
        onClick={handleAddDataClicked}
        sx={{ alignSelf: "start" }}
        disabled={props.form.getValues("datasets").length === 0}
      >
        Add Data
      </Button>

      <Table
        tableId="metrics"
        data={rows}
        columns={columns}
        columnVisibility={{
          actions: props.enableActions,
        }}
        columnPinning={{
          left: ["Date"],
          right: ["Actions"],
        }}
        sorting={[{ id: "Date", desc: true }]}
        sx={{ ...props.sx }}
      />
    </>
  );
};

export { MetricTable };
